import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
} from "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { Briefcase, MapPin, Grid3x3, List, Filter, Calendar } from "lucide-react";

const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  return new Date(timestamp.seconds * 1000).toLocaleDateString();
};

const MyJobs = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list");
  const [dateFilter, setDateFilter] = useState("all");

  useEffect(() => {
    if (!user) return;

    const db = getFirestore(firebaseApp);
    const q = query(
      collection(db, "hrJobs"),
      where("hrId", "==", user.id),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const jobList = snapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() })
      );
      setJobs(jobList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Filter jobs based on date
  useEffect(() => {
    const now = new Date();
    let filtered = [...jobs];

    if (dateFilter === "today") {
      filtered = jobs.filter((job) => {
        const jobDate = new Date(job.createdAt.seconds * 1000);
        return jobDate.toDateString() === now.toDateString();
      });
    } else if (dateFilter === "week") {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = jobs.filter((job) => {
        const jobDate = new Date(job.createdAt.seconds * 1000);
        return jobDate >= weekAgo;
      });
    } else if (dateFilter === "month") {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = jobs.filter((job) => {
        const jobDate = new Date(job.createdAt.seconds * 1000);
        return jobDate >= monthAgo;
      });
    }

    setFilteredJobs(filtered);
  }, [jobs, dateFilter]);

  if (loading) {
    return <div className="text-center py-10">Loading your jobs...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-10">
      {/* Header with Title and Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Briefcase className="w-8 h-8 mr-3 text-red-600" />
            My Posted Jobs
          </h1>
          <p className="text-gray-600 mt-1">Manage and track your job postings</p>
        </div>
        <Link
          to="/hr/jd-upload"
          className="px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-900 text-white font-semibold rounded-xl hover:from-red-800 hover:to-red-950 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center"
        >
          Post New Job
        </Link>
      </div>

      {/* Filters and View Toggle */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Date Filter */}
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Filter className="w-5 h-5 text-gray-500" />
            </div>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 bg-white text-gray-700 min-w-[150px] transition-all"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
            <span className="text-sm font-medium text-gray-500 hidden sm:inline-block border-l border-gray-200 pl-3">
              {filteredJobs.length} result{filteredJobs.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-gray-100/80 rounded-xl p-1.5 border border-gray-200/50">
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-lg transition-all ${viewMode === "list"
                  ? "bg-white text-red-600 shadow-sm ring-1 ring-black/5"
                  : "text-gray-500 hover:text-gray-900 hover:bg-white/50"
                }`}
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-lg transition-all ${viewMode === "grid"
                  ? "bg-white text-red-600 shadow-sm ring-1 ring-black/5"
                  : "text-gray-500 hover:text-gray-900 hover:bg-white/50"
                }`}
            >
              <Grid3x3 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Jobs Display */}
      {filteredJobs.length === 0 ? (
        <div className="text-center py-20 bg-white border border-gray-200 rounded-2xl shadow-sm">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">No jobs found</h3>
          <p className="text-gray-500 mb-6 max-w-xs mx-auto">
            {jobs.length === 0
              ? "You haven't posted any jobs yet. Start by creating your first job posting."
              : "No jobs match your selected date filter."}
          </p>
          {jobs.length === 0 && (
            <Link
              to="/hr/jd-upload"
              className="inline-flex items-center text-red-700 font-semibold hover:text-red-800 hover:underline"
            >
              Post a new job
            </Link>
          )}
        </div>
      ) : viewMode === "list" ? (
        <div className="space-y-4">
          {filteredJobs.map((job) => (
            <Link
              to={`/hr/my-jobs/${job.id}`}
              key={job.id}
              className="group block bg-white p-5 border border-gray-200 rounded-2xl shadow-sm hover:shadow-md hover:border-red-200 transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center shrink-0 border border-red-100 group-hover:bg-red-100 transition-colors">
                    <Briefcase className="w-6 h-6 text-red-700" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 group-hover:text-red-700 transition-colors">
                      {job.jobTitle}
                    </h2>
                    <div className="flex items-center gap-4 mt-1">
                      <div className="flex items-center capitalize text-sm text-gray-500 font-medium">
                        <MapPin className="w-3.5 h-3.5 mr-1" />
                        {job.location || "Remote"}
                      </div>
                      <div className="flex items-center text-sm text-gray-500 font-medium">
                        <Calendar className="w-3.5 h-3.5 mr-1" />
                        {formatDate(job.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-right hidden sm:block">
                  <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold capitalize">
                    {job.jobPosition}
                  </span>
                  <div className="mt-2 text-xs font-medium text-gray-500">
                    {job.experience} years exp
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredJobs.map((job) => (
            <Link
              to={`/hr/my-jobs/${job.id}`}
              key={job.id}
              className="group block bg-white p-6 border border-gray-200 rounded-2xl shadow-sm hover:shadow-lg hover:border-red-200 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-red-50 rounded-xl border border-red-100 group-hover:bg-red-100 transition-colors">
                  <Briefcase className="w-6 h-6 text-red-700" />
                </div>
                <span className="text-[10px] font-bold bg-gray-100 text-gray-600 px-2.5 py-1 rounded-lg flex items-center gap-1">
                  {formatDate(job.createdAt)}
                </span>
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1 group-hover:text-red-700 transition-colors">
                {job.jobTitle}
              </h3>

              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 font-medium">Position</span>
                  <span className="text-gray-900 font-semibold capitalize">{job.jobPosition}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 font-medium">Experience</span>
                  <span className="text-gray-900 font-semibold">{job.experience} Years</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-center text-sm font-semibold text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                View Details →
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyJobs;
